import React, { useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useFeatures } from '@atc/features';

import { Col, Row, Section } from 'reaxl';
import { clickTypes, sendClick, sendImpressions } from 'reaxl-analytics';
import { InventoryCarousel } from 'reaxl-organisms';

import saveListing from '@/utilities/saveListing';

import { inventoryClick, inventoryImpressions } from '@/analytics/srpAnalyticsHandlers';

import savedInventoryDuck from '@/ducks/savedInventoryDuck.js';
import srpActiveInteractionDuck from '@/ducks/srp/srpActiveInteractionDuck.js';
import vdpSimilarListingsDuck from '@/ducks/vdp/vdpSimilarListingsDuck.js';

import useVdpNavigation from '@/hooks/useVdpNavigation';

const baseIconUrl = '/cm-api/content/static/img/icon';

const getUrgencyDriver = (spotlight) => {
    const {
        vehicleHighlight: {
            urgencyDriverType,
            urgencyDriverValue,
        } = {},
    } = spotlight;

    if (urgencyDriverType && urgencyDriverValue) {
        switch (urgencyDriverType) {
            case 'DEALER_INCENTIVE':
                return { type: 'custom', text: urgencyDriverValue, color: 'success', imgSrc: `${baseIconUrl}/promocard/detailed.svg` };
            case 'WARRANTY':
                return { type: 'warranty', text: 'Limited Warranty', subText: urgencyDriverValue, color: 'success', imgSrc: `${baseIconUrl}/promocard/most_reliable.svg` };
            case 'RETURN':
                return { type: 'return', text: 'Return Policy', subText: urgencyDriverValue, color: 'success', imgSrc: `${baseIconUrl}/promocard/most_reliable.svg` };
            case 'INCENTIVES':
                return { type: 'incentives', text: urgencyDriverValue, color: 'success', imgSrc: `${baseIconUrl}/promocard/affordable_new_cars.svg` };
            default:
                return {};
        }
    }
    return {};
};

function SpotlightCarouselContainer({ spotlights }) {

    const dispatch = useDispatch();

    const navigateToVdp = useVdpNavigation();

    const {
        personalization: [isSaveEnabled],
    } = useFeatures(['personalization']);

    const savedListingIds = useSelector(savedInventoryDuck.selectors.getSavedInventoryIds);

    const modifiedSpotlights = spotlights.map((spotlight) => {
        const spotlightCopy = { ...spotlight };

        // TODO: Check with Tomeko on this: Remove payments from the UI for this carousel?
        if (spotlightCopy.pricingDetail.payment) {
            delete spotlightCopy.pricingDetail.payment;
        }

        const urgencyDriver = getUrgencyDriver(spotlightCopy);

        return {
            ...spotlightCopy,
            urgencyDriver,
        };
    });

    const handleSendImpressions = useCallback(({ index, selectedSpotlightIds, startingIndex }) => sendImpressions({
        name: inventoryImpressions,
        data: {
            cmp: 'sim',
            selectedItemIds: selectedSpotlightIds,
            pixallData: {
                rank: index + 1,
            },
            startingIndex,
        },
    }), []);

    // The callback to handle events that will fire once the carousel is ready
    const handleReady = useCallback((index, selectedCells) => {
        const selectedSpotlights = spotlights.slice(index, (index + selectedCells.length));
        const selectedSpotlightIds = selectedSpotlights.map(({ id }) => id);

        handleSendImpressions({ index, selectedSpotlightIds, startingIndex: selectedCells.length * index });
    }, [handleSendImpressions, spotlights]);

    // The callback to handle events that will fire once new slide settles in to the viewable frame
    const handleSettle = useCallback((index, _direction, _width, selectedCells) => {
        const subArrayIndex = selectedCells.length * index;
        const selectedSpotlights = spotlights.slice(subArrayIndex, (subArrayIndex + selectedCells.length));
        const selectedSpotlightIds = selectedSpotlights.map(({ id }) => id);

        handleSendImpressions({ index, selectedSpotlightIds, subArrayIndex });
    }, [handleSendImpressions, spotlights]);

    const handleSaveToggle = useCallback(async (event, savedListing, wasSaved) => {
        dispatch(
            saveListing({
                event,
                listing: savedListing,
                parentId: 'sim',
                saveType: { carousel: true },
                sendClick,
                wasSaved,
            })
        );
    }, [dispatch]);

    const handleInventoryListingClick = useCallback(async (event, selectedListing) => {
        // need to stop propagation and prevent default to allow anchors within listing for SEO purposes while using the
        // correct handlers
        event.stopPropagation();
        event.preventDefault();

        dispatch(srpActiveInteractionDuck.creators.setKeys({
            showSpinningIndicator: true,
        }));

        const targetClassName = event && event.target && event.target.className || [];
        const isSaveClick = targetClassName.includes('glyphicon-heart-o');
        const isSelectClick = targetClassName.includes('input-label') && event.target.innerHTML === 'Select';

        if (!(isSaveClick || isSelectClick) && selectedListing) {
            await sendClick(inventoryClick, event, {
                friendlyPageName: 'Search Results Page',
                inventoryId: selectedListing.id,
                par: 'sim',
                pixallData: {
                    eventSource: 'vehicle',
                    pageid: 'fyc-srl',
                    listingType: 'highlight',
                },
            });

            if (selectedListing.id) {
                dispatch(vdpSimilarListingsDuck.creators.clear());

                navigateToVdp({
                    listingId: selectedListing.id,
                    clickType: 'highlight',
                });
            } else {
                return false;
            }
        }

        return true;
    }, [dispatch, navigateToVdp]);

    const carouselSettings = {
        onReady: handleReady,
        fullWidthScroll: true,
        visibilityThreshold: 0.9,
        fullWidthScrollXs: true,
    };

    return (
        <Col xs={12}>
            <Section
                alignment="left"
                uiContext="section-5"
                heading="Picked for You"
                className="margin-bottom-5"
            >
                <Row
                    className="padding-left-2"
                >
                    <InventoryCarousel
                        key="spotlight-inventory-carousel"
                        listings={modifiedSpotlights}
                        listingClassName="bg-brand-secondary"
                        clickType={clickTypes.SIMILAR_INVENTORY}
                        carouselSettings={carouselSettings}
                        onSettle={handleSettle}
                        onSaveToggle={isSaveEnabled && handleSaveToggle}
                        onInventoryListingClick={handleInventoryListingClick}
                        savedListingIds={savedListingIds}
                        showFooter={false}
                        uiContext="snapshot"
                    />
                </Row>
            </Section>
        </Col>
    );
}

export default SpotlightCarouselContainer;
